/* os-walk — the scrolled walk-through of a Tool screen.

   The CoffeeOS page already puts a working simulator in front of the
   reader. This walks them through it: as the page scrolls, one part of
   the screen lights up and a line explains it. Scroll back and it runs
   in reverse.

   One rule holds it together: nothing accumulates. Every frame reads a
   single number — how far through the walk the page has scrolled, 0 to
   1 — and derives the step, the spotlight and the caption from it.
   There is no step++ anywhere in this file, which is why scrolling
   backwards works for free and why a repaint in the middle cannot
   strand it.

   The simulator stays sandboxed. os-sim-agent.js is appended to its
   HTML before the srcdoc is set — added, never spliced — and the two
   halves talk by postMessage: the frame reports where its parts are,
   the parent says which to light. The parent never reads its DOM.

   Role handling. The walk sits on a [data-act-scene] element, so
   os.js hides and shows it by role exactly like the illustration it
   replaced, and a reader whose persona never sees it never downloads
   the simulator. Note that changing role on this page also scrolls the
   reader to the top — see changeRole in os.js — so a role change
   returns the walk to its first step rather than holding position. */
(function () {
  "use strict";

  var D = document;
  var REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ── the simulator's HTML, fetched once, only when needed ──── */
  var pending = {};
  function loadSim(key, done) {
    var bag = window.OS_SIM || {};
    if (bag[key]) return done(bag[key]);
    var url = (window.OS_SIM_URLS || {})[key];
    if (!url) return done(null);
    if (pending[key]) { pending[key].push(done); return; }
    pending[key] = [done];
    var s = D.createElement("script");
    s.src = url;
    s.async = true;
    s.onload = function () {
      var html = (window.OS_SIM || {})[key] || null;
      pending[key].forEach(function (fn) { fn(html); });
      pending[key] = null;
    };
    s.onerror = function () {
      if (window.console) console.warn("[OS] walk: simulator failed to load:", key, url);
      pending[key].forEach(function (fn) { fn(null); });
      pending[key] = null;
    };
    D.head.appendChild(s);
  }

  /* Whatever is pinned above the page. Measured rather than assumed:
     the header is a different height on a phone, and guessing it puts
     the walk's own headline underneath it. */
  function topbarH() {
    var el = D.querySelector(".os-header");
    if (!el) return 0;
    var cs = window.getComputedStyle(el);
    if (cs.position !== "fixed" && cs.position !== "sticky") return 0;
    return Math.ceil(el.getBoundingClientRect().height);
  }

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  function boot(walk) {
    var C = window.OS_CONTENT;
    if (!C) return;

    var key     = walk.getAttribute("data-os-walk") || "particle";
    var frame   = walk.querySelector("[data-walk-frame]");
    var track   = walk.querySelector("[data-walk-track]");
    var capWrap = walk.querySelector("[data-walk-caps]");
    var spot    = walk.querySelector("[data-walk-spot]");
    if (!frame || !track) return;

    var iframe = null, parts = null, taken = false, mounted = false;
    var steps = [], drawn = -1, builtFor = null;
    var designW = 366, designH = 733;

    function role() {
      return (window.OS && window.OS.currentId) ? window.OS.currentId() : "roaster";
    }

    function copyFor(id) {
      var deck = (C.COFFEEOS && C.COFFEEOS.walk && C.COFFEEOS.walk[key]) || null;
      if (!deck) return [];
      return C.pick(deck, id) || [];
    }

    /* ── mount, the first time it is actually on screen ──────── */
    function mount() {
      if (mounted) return;
      mounted = true;
      loadSim(key, function (html) {
        if (!html || !window.OS_SIM_AGENT) {
          if (window.console) console.warn("[OS] walk: no simulator or no agent for", key);
          return;
        }
        /* Appended before the closing body tag. Never a regex that
           cuts the file about — that is how the Moment simulator lost
           half its media queries once already. */
        var tag = "<script>" + window.OS_SIM_AGENT + "<\/script>";
        var at = html.lastIndexOf("</body>");
        var doc = at > -1 ? html.slice(0, at) + tag + html.slice(at) : html + tag;

        iframe = D.createElement("iframe");
        iframe.className = "os-walk__doc";
        iframe.setAttribute("title", walk.getAttribute("data-walk-title") || "CoffeeOS");
        iframe.setAttribute("loading", "lazy");
        /* No allow-same-origin, exactly as os-cos.js mounts them. */
        iframe.setAttribute("sandbox", "allow-scripts");
        iframe.srcdoc = doc;
        frame.appendChild(iframe);
      });
    }

    window.addEventListener("message", function (e) {
      if (!iframe || !iframe.contentWindow || e.source !== iframe.contentWindow) return;
      var d = e.data;
      if (!d || !d.os) return;
      if (d.os === "sim-parts") { parts = d.parts; drawn = -1; draw(); }
      if (d.os === "sim-height") {
        var px = parseInt(d.px, 10);
        if (px > 80 && px < 4000) { designH = px; fit(); }
      }
      if (d.os === "sim-taken") {
        /* They have started using it. Stop directing and get out of
           the way — the same hand-over the shot sequences do. */
        taken = true;
        walk.classList.add("is-taken");
        tell(null);
        if (spot) spot.style.opacity = "0";
      }
    });

    function tell(part) {
      if (!iframe || !iframe.contentWindow) return;
      try { iframe.contentWindow.postMessage({ os: "sim-step", part: part }, "*"); }
      catch (err) {}
    }

    /* ── fit ─────────────────────────────────────────────────
       The screen is a fixed size inside the frame and is scaled to
       the room the window leaves. Letting it set its own height is
       what pushes the headline off the top of a short window. */
    function fit() {
      var grid = walk.querySelector(".os-walk__grid");
      if (!grid || walk.hidden) return;

      var stacked = window.innerWidth < 900;
      var textEl = walk.querySelector(".os-walk__text");
      var textH = stacked && textEl ? textEl.getBoundingClientRect().height + 24 : 0;
      var top = topbarH();
      walk.style.setProperty("--walk-top", (top + 14) + "px");
      var target = window.innerHeight - top - 34;
      var availW = stacked ? window.innerWidth - 44 : designW;

      var s = Math.min(1, availW / designW, (target - textH) / designH);
      if (!isFinite(s) || s <= 0) s = 0.2;
      apply(s);

      /* Then check the answer rather than trusting the arithmetic.
         The grid's real height is the only thing that decides whether
         the headline survives, so measure it and correct. */
      for (var n = 0; n < 3; n++) {
        var got = grid.getBoundingClientRect().height;
        if (got <= target) break;
        s = Math.max(0.2, s * (target / got));
        apply(s);
      }

      function apply(v) {
        walk.style.setProperty("--s", v);
        walk.style.setProperty("--dw", designW + "px");
        walk.style.setProperty("--dh", designH + "px");
      }
    }

    function progress() {
      var r = track.getBoundingClientRect();
      var travel = r.height - window.innerHeight;
      if (travel <= 0) return 0;
      return Math.min(1, Math.max(0, -r.top / travel));
    }

    function draw() {
      if (walk.hidden) return;
      var id = role();
      steps = copyFor(id);
      if (!steps.length) return;

      var i = Math.round(progress() * (steps.length - 1));
      i = Math.min(steps.length - 1, Math.max(0, i));

      /* Rebuilt when the role changes, not when the number of steps
         changes — every family has the same beats, so counting them
         would never notice a swap. */
      if (capWrap && builtFor !== id) {
        builtFor = id;
        capWrap.innerHTML = steps.map(function (s, n) {
          return '<div class="os-walk__cap" data-i="' + n + '">' +
                   '<p class="os-walk__k">' + esc(s.k) + "</p>" +
                   "<h3>" + esc(s.h) + "</h3>" +
                   "<p>" + esc(s.b) + "</p>" +
                 "</div>";
        }).join("");
        drawn = -1;
        fit();
      }

      if (i === drawn) return;
      drawn = i;

      var caps = capWrap ? capWrap.querySelectorAll(".os-walk__cap") : [];
      for (var n = 0; n < caps.length; n++) caps[n].classList.toggle("is-on", n === i);

      if (taken) return;
      tell(steps[i].part);
      spotlight(steps[i].part);
    }

    /* The parent's half of the highlight: everything outside the named
       rectangle is dimmed, drawn from the rectangle the frame
       reported, because the parent cannot measure inside it. */
    function spotlight(name) {
      if (!spot) return;
      var r = parts && parts[name];
      if (!r) { spot.style.opacity = "0"; return; }
      var pad = 1.2;
      spot.style.opacity = "1";
      spot.style.setProperty("--x", (r.x - pad) + "%");
      spot.style.setProperty("--y", (r.y - pad) + "%");
      spot.style.setProperty("--w", (r.w + pad * 2) + "%");
      spot.style.setProperty("--h", (r.h + pad * 2) + "%");
    }

    var queued = false;
    function onScroll() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; draw(); });
    }

    if (REDUCED) {
      /* No pin, no scrubbing. Every caption at once, and the frame is
         the simulator it always was. */
      walk.classList.add("is-still");
      steps = copyFor(role());
      if (capWrap) {
        capWrap.innerHTML = steps.map(function (s) {
          return '<div class="os-walk__cap is-on">' +
                   '<p class="os-walk__k">' + esc(s.k) + "</p><h3>" + esc(s.h) +
                   "</h3><p>" + esc(s.b) + "</p></div>";
        }).join("");
      }
    }

    /* Nothing is downloaded until the walk is genuinely near. A hidden
       scene has no layout box, so it never intersects — which is how
       the page already keeps one persona from paying for another's
       simulator. */
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (es, io) {
        es.forEach(function (e) {
          if (!e.isIntersecting) return;
          mount(); fit(); draw();
          io.disconnect();
        });
      }, { rootMargin: "600px 0px" }).observe(walk);
    } else {
      mount();
    }

    if (!REDUCED) {
      window.addEventListener("scroll", onScroll, { passive: true });
    }
    window.addEventListener("resize", function () { fit(); draw(); });

    /* os.js shows and hides this element by role. When it comes back
       it needs measuring again, because a hidden element has no size. */
    D.addEventListener("os:role", function () {
      drawn = -1;
      window.setTimeout(function () {
        if (walk.hidden) return;
        mount(); fit(); draw();
      }, 80);
    });

    fit();
    draw();
  }

  function init() {
    [].forEach.call(D.querySelectorAll("[data-os-walk]"), boot);
  }
  if (D.readyState === "loading") D.addEventListener("DOMContentLoaded", init);
  else init();
})();
